// @ts-nocheck Chakra v1 AlertDialogContent union depth in strict IDE checks.
import React from "react";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  FormControl,
  FormLabel,
  Input,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import type { TradeAccountOption } from "../../contexts/ActiveBrokerAccountContext";
import { isTradeAccountCsvOnlyForUi } from "../../utils/tradeAccountSource";

export interface RemoveAccountAlertDialogProps {
  deleteTarget: TradeAccountOption | null;
  deleteConfirmText: string;
  setDeleteConfirmText: (v: string) => void;
  deleteConfirmMatches: boolean;
  deleteTargetIsActive: boolean;
  isDeletingAccount: boolean;
  deleteCancelRef: React.RefObject<HTMLButtonElement>;
  deleteConfirmInputRef: React.RefObject<HTMLInputElement>;
  borderColor: string;
  mutedColor: string;
  labelColor: string;
  /** When set, user must type this exact string (e.g. display name). Defaults to account code. */
  confirmPhrase?: string;
  onCancel: () => void;
  onConfirm: () => void;
}

export function RemoveAccountAlertDialog({
  deleteTarget,
  deleteConfirmText,
  setDeleteConfirmText,
  deleteConfirmMatches,
  deleteTargetIsActive,
  isDeletingAccount,
  deleteCancelRef,
  deleteConfirmInputRef,
  borderColor,
  mutedColor,
  labelColor,
  confirmPhrase,
  onCancel,
  onConfirm,
}: RemoveAccountAlertDialogProps) {
  const isCsvRemoval = deleteTarget ? isTradeAccountCsvOnlyForUi(deleteTarget) : false;
  const requiredConfirmText = deleteTarget
    ? confirmPhrase?.trim() || deleteTarget.accountCode
    : "";
  const confirmWithAccountName = Boolean(confirmPhrase?.trim());

  return (
    <AlertDialog
      isOpen={!!deleteTarget}
      leastDestructiveRef={deleteCancelRef}
      onClose={isDeletingAccount ? () => undefined : onCancel}
      closeOnOverlayClick={!isDeletingAccount}
      closeOnEsc={!isDeletingAccount}
      isCentered
    >
      <AlertDialogOverlay />
      <AlertDialogContent>
        <AlertDialogHeader fontFamily="'Urbanist', sans-serif" fontSize="lg" color="#38BBD1" pr={10}>
          Remove account data from Tessera?
        </AlertDialogHeader>
        <AlertDialogBody fontFamily="'IBM Plex Sans', sans-serif">
          {deleteTarget ? (
            <>
              {isCsvRemoval ? (
                <Text fontSize="sm" color={mutedColor} mb={3}>
                  This deletes Tessera data for{" "}
                  <Text as="span" fontWeight="600" color={labelColor}>
                    {deleteTarget.brokerDisplayName || deleteTarget.brokerName}
                  </Text>{" "}
                  for this account code only. It does not close your real brokerage account or change anything at your
                  broker. Tessera removes stored trades and activity for this code, including history brought in via CSV
                  imports.
                </Text>
              ) : (
                <Text fontSize="sm" color={mutedColor} mb={3}>
                  This deletes Tessera data for{" "}
                  <Text as="span" fontWeight="600" color={labelColor}>
                    {deleteTarget.brokerDisplayName || deleteTarget.brokerName}
                  </Text>{" "}
                  for this account code only. It does not close your real brokerage account, log you out of the broker, or
                  remove saved API credentials. To disconnect or delete credentials, use Broker Management in Profile
                  (Remove credentials / disconnect).
                </Text>
              )}
              <Text fontSize="sm" fontWeight="600" color={labelColor} mb={2}>
                This will delete or reset:
              </Text>
              <UnorderedList fontSize="sm" color={mutedColor} spacing={1} mb={3} pl={4}>
                <ListItem>
                  {isCsvRemoval
                    ? "Trades and cash transfers for this account code (including CSV-imported history)"
                    : "Trades and cash transfers for this account code"}
                </ListItem>
                <ListItem>Custom display names, PnL milestones, and milestone notifications for this account</ListItem>
                <ListItem>Saved layout and settings tied to this account</ListItem>
                <ListItem>Reports and backtests (including linked notes) for this account</ListItem>
              </UnorderedList>
              {deleteTargetIsActive ? (
                <Text fontSize="sm" color="orange.400" mb={3}>
                  This is your active account. After removal, Tessera will pick another account or none until you choose
                  again.
                </Text>
              ) : null}
              <Text fontSize="sm" color={labelColor} mb={2}>
                Type the {confirmWithAccountName ? "account name" : "account code"} exactly to confirm:
              </Text>
              <Text
                as="kbd"
                display="block"
                fontSize="sm"
                fontFamily={confirmWithAccountName ? "'IBM Plex Sans', sans-serif" : "mono"}
                px={2}
                py={1}
                borderRadius="md"
                borderWidth="1px"
                borderColor={borderColor}
                mb={3}
                wordBreak="break-word"
              >
                {requiredConfirmText}
              </Text>
              <FormControl>
                <FormLabel fontSize="xs" textTransform="uppercase" color={mutedColor}>
                  {confirmWithAccountName ? "Account name" : "Account code"}
                </FormLabel>
                <Input
                  ref={deleteConfirmInputRef}
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={
                    confirmWithAccountName ? "Type account name" : "Paste or type account code"
                  }
                  fontFamily={confirmWithAccountName ? "'IBM Plex Sans', sans-serif" : "mono"}
                  size="sm"
                  isDisabled={isDeletingAccount}
                  autoComplete="off"
                />
              </FormControl>
            </>
          ) : null}
        </AlertDialogBody>
        <AlertDialogFooter>
          <Button ref={deleteCancelRef} onClick={onCancel} isDisabled={isDeletingAccount}>
            Cancel
          </Button>
          <Button
            colorScheme="red"
            ml={3}
            onClick={() => void onConfirm()}
            isLoading={isDeletingAccount}
            isDisabled={!deleteConfirmMatches || isDeletingAccount}
          >
            Remove account data
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
